import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Revenu, Depense, SoldeDTO, AuditLogEntry, GroupeDoublon, VenteRequest, ModeleTransaction, SyntheseFinance, AuditFiltre } from '../../models/finance.model';

/** Page de la liste Finances (filtres et totaux calculés côté serveur) */
export interface PageTransactions {
  items: (Revenu & Depense & { type: 'revenu' | 'depense' })[];
  /** Opérations correspondant aux filtres */
  total: number;
  /** Opérations chargées avant filtres */
  totalTout: number;
  offset: number;
  revenus: number;
  depenses: number;
  nbActives: number;
  totauxJour: { [date: string]: { revenus: number; depenses: number; nb: number } };
  categories: string[];
  nbImpayes: number;
  resteClients: number;
  resteFournisseurs: number;
  /** Position de la ligne demandée (focus) : -1 hors filtres, null si inexistante */
  focusIndex: number | null;
}

export interface CriteresTransactions {
  q?: string; type?: string; impayes?: boolean; annulees?: boolean; categorie?: string; mode?: string;
  dateDebut?: string; dateFin?: string; offset?: number; limit?: number; focus?: string;
}

/** Filtres de la page Finance transmis aux exports */
export interface ExportFiltres { type?: string; categorie?: string; mode?: string; q?: string; impayes?: boolean; annulees?: boolean; }

@Injectable({ providedIn: 'root' })
export class FinanceService {

  constructor(private http: HttpClient) {}

  // ── Revenus ────────────────────────────────────────────────────────────────

  getRevenus(fermeId: number, annulees = false): Observable<Revenu[]> {
    return this.http.get<Revenu[]>(`${environment.BASE_URL}/api/finances/revenus`, { params: annulees ? { fermeId, annulees } : { fermeId } });
  }

  getRevenu(id: number): Observable<Revenu> {
    return this.http.get<Revenu>(`${environment.BASE_URL}/api/finances/revenus/${id}`);
  }

  createRevenu(dto: Revenu): Observable<Revenu> {
    return this.http.post<Revenu>(`${environment.BASE_URL}/api/finances/revenus`, dto);
  }

  updateRevenu(id: number, dto: Revenu): Observable<Revenu> {
    return this.http.put<Revenu>(`${environment.BASE_URL}/api/finances/revenus/${id}`, dto);
  }

  deleteRevenu(id: number, motif?: string): Observable<void> {
    return this.http.delete<void>(`${environment.BASE_URL}/api/finances/revenus/${id}`, { params: motif ? { motif } : {} });
  }

  /** Reçu d'une vente archivé dans Documents › Reçus, avec lien de partage */
  archiverRecuVente(id: number): Observable<{ documentId: number; lienPublic?: string }> {
    return this.http.post<{ documentId: number; lienPublic?: string }>(`${environment.BASE_URL}/api/finances/revenus/${id}/recu`, {});
  }

  /** Vente rapide (Finance ou module) : événement de module + effectif + revenu en une saisie */
  vendre(v: VenteRequest): Observable<Revenu> {
    return this.http.post<Revenu>(`${environment.BASE_URL}/api/ventes`, v);
  }

  // ── Dépenses ───────────────────────────────────────────────────────────────

  getDepenses(fermeId: number, annulees = false): Observable<Depense[]> {
    return this.http.get<Depense[]>(`${environment.BASE_URL}/api/finances/depenses`, { params: annulees ? { fermeId, annulees } : { fermeId } });
  }

  getDepense(id: number): Observable<Depense> {
    return this.http.get<Depense>(`${environment.BASE_URL}/api/finances/depenses/${id}`);
  }

  createDepense(dto: Depense): Observable<Depense> {
    return this.http.post<Depense>(`${environment.BASE_URL}/api/finances/depenses`, dto);
  }

  updateDepense(id: number, dto: Depense): Observable<Depense> {
    return this.http.put<Depense>(`${environment.BASE_URL}/api/finances/depenses/${id}`, dto);
  }

  deleteDepense(id: number, motif?: string): Observable<void> {
    return this.http.delete<void>(`${environment.BASE_URL}/api/finances/depenses/${id}`, { params: motif ? { motif } : {} });
  }

  // ── Modèles de transaction (favoris) ─────────────────────────────────────

  getModeles(fermeId: number): Observable<ModeleTransaction[]> {
    return this.http.get<ModeleTransaction[]>(`${environment.BASE_URL}/api/finances/modeles`, { params: { fermeId } });
  }

  createModele(m: ModeleTransaction): Observable<ModeleTransaction> {
    return this.http.post<ModeleTransaction>(`${environment.BASE_URL}/api/finances/modeles`, m);
  }

  deleteModele(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.BASE_URL}/api/finances/modeles/${id}`);
  }

  modeleUtilise(id: number): Observable<void> {
    return this.http.post<void>(`${environment.BASE_URL}/api/finances/modeles/${id}/utilise`, {});
  }

  /** N dernières transactions (tableau de bord) — évite de télécharger tout l'historique */
  getRecentes(fermeId: number, limit = 5): Observable<{ revenus: Revenu[]; depenses: Depense[] }> {
    return this.http.get<{ revenus: Revenu[]; depenses: Depense[] }>(
      `${environment.BASE_URL}/api/finances/recentes`, { params: { fermeId, limit } });
  }

  // ── Revue des doublons probables ─────────────────────────────────────────

  getDoublons(fermeId: number): Observable<GroupeDoublon[]> {
    return this.http.get<GroupeDoublon[]>(`${environment.BASE_URL}/api/finances/doublons`, { params: { fermeId } });
  }

  marquerPasDoublon(type: 'revenu' | 'depense', id: number): Observable<void> {
    const seg = type === 'revenu' ? 'revenus' : 'depenses';
    return this.http.put<void>(`${environment.BASE_URL}/api/finances/${seg}/${id}/doublon-verifie`, {});
  }

  // ── Liste paginée ─────────────────────────────────────────────────────────

  getTransactions(fermeId: number, c: CriteresTransactions): Observable<PageTransactions> {
    const params: { [k: string]: string | number | boolean } = { fermeId };
    Object.entries(c).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== false) params[k] = v; });
    return this.http.get<PageTransactions>(`${environment.BASE_URL}/api/finances/transactions`, { params });
  }

  // ── Solde / Snapshot ──────────────────────────────────────────────────────

  getSolde(fermeId: number): Observable<SoldeDTO> {
    return this.http.get<SoldeDTO>(`${environment.BASE_URL}/api/finances/solde`, { params: { fermeId } });
  }

  // ── Audit Log ─────────────────────────────────────────────────────────────

  getAuditLog(entityType: string, entityId: number): Observable<AuditLogEntry[]> {
    return this.http.get<AuditLogEntry[]>(`${environment.BASE_URL}/api/audit`, {
      params: { entityType, entityId }
    });
  }

  // ── Export Excel ───────────────────────────────────────────────────────────

  /** Filtres facultatifs de la page Finance : type (revenu|depense), categorie, mode, q, impayes, annulees */
  exportExcel(fermeId: number, dateDebut?: string, dateFin?: string, filtres: ExportFiltres = {}): Observable<Blob> {
    const params: Record<string, any> = { fermeId };
    if (dateDebut) params['dateDebut'] = dateDebut;
    if (dateFin)   params['dateFin']   = dateFin;
    Object.entries(filtres).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== false) params[k] = v; });
    return this.http.get(`${environment.BASE_URL}/api/finances/export`, {
      params, responseType: 'blob'
    });
  }

  // ── Comptabilité (Lot 6) ─────────────────────────────────────

  /** Annule une transaction (reste visible, hors totaux, motif obligatoire) */
  annuler(type: 'revenu' | 'depense', id: number, motif: string): Observable<void> {
    const seg = type === 'revenu' ? 'revenus' : 'depenses';
    return this.http.post<void>(`${environment.BASE_URL}/api/finances/${seg}/${id}/annuler`, {}, { params: { motif } });
  }

  getSynthese(fermeId: number, debut: string, fin: string): Observable<SyntheseFinance> {
    return this.http.get<SyntheseFinance>(`${environment.BASE_URL}/api/finances/synthese`, { params: { fermeId, dateDebut: debut, dateFin: fin } });
  }

  ajusterCaisse(fermeId: number, mode: string, soldeReel: number, note?: string): Observable<any> {
    const params: any = { fermeId, mode, soldeReel };
    if (note) params.note = note;
    return this.http.post<any>(`${environment.BASE_URL}/api/finances/caisse/ajuster`, {}, { params });
  }

  /** mois au format AAAA-MM */
  rapportMensuel(fermeId: number, mois: string): Observable<Blob> {
    return this.http.get(`${environment.BASE_URL}/api/rapports/mensuel`, { params: { fermeId, mois }, responseType: 'blob' });
  }

  archiverRapportMensuel(fermeId: number, mois: string): Observable<{ documentId: number; lienPublic: string }> {
    return this.http.post<{ documentId: number; lienPublic: string }>(`${environment.BASE_URL}/api/rapports/mensuel`, {}, { params: { fermeId, mois } });
  }

  exportJournal(fermeId: number, dateDebut?: string, dateFin?: string, filtres: ExportFiltres = {}): Observable<Blob> {
    const params: any = { fermeId };
    Object.entries(filtres).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== false && k !== 'annulees') params[k] = v; });
    if (dateDebut) params.dateDebut = dateDebut;
    if (dateFin) params.dateFin = dateFin;
    return this.http.get(`${environment.BASE_URL}/api/finances/export-journal`, { params, responseType: 'blob' });
  }

  getHistoriqueFerme(fermeId: number, f: AuditFiltre = {}): Observable<AuditLogEntry[]> {
    const params: any = { fermeId };
    Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params[k] = v; });
    return this.http.get<AuditLogEntry[]>(`${environment.BASE_URL}/api/audit/ferme`, { params });
  }
}
